import { SimplePaletteColorOptions, useTheme } from "@mui/material";

export enum ColorPalette {
  PRIMARY = "primary",
  SECONDARY = "secondary",
  NEUTRAL = "neutral",
  TEXT = "text",
  ERROR = "error",
  WARNING = "warning",
  SUCCESS = "success",
  INFO = "info",
  BACKGROUND_COLOR = "backgroundColor",
}

export enum ColorThemeToken {
  LIGHT = "light",
  DARK = "dark",
}

export enum ColorToken {
  MAIN = "main",
  LIGHT = "light",
  DARK = "dark",
  CONTRAST_TEXT = "contrastText",
  COMPONENT = "component",
  SECTION = "section",
  SCREEN = "screen",
  GRADIENT = "gradient",
}

export type ColorSchema = Record<
  ColorThemeToken,
  Record<ColorPalette, SimplePaletteColorOptions>
>;

export const ColorSchema: ColorSchema = {
  [ColorThemeToken.LIGHT]: {
    [ColorPalette.PRIMARY]: {
      [ColorToken.MAIN]: "#2B478B",
      [ColorToken.DARK]: "#213870",
      [ColorToken.LIGHT]: "#738DE4",
      [ColorToken.CONTRAST_TEXT]: "#FFFFFF",
    },
    [ColorPalette.SECONDARY]: {
      [ColorToken.MAIN]: "#EFF0F9",
      [ColorToken.DARK]: "#D2D6F3",
      [ColorToken.LIGHT]: "#F3F9FF",
      [ColorToken.CONTRAST_TEXT]: "#213870",
    },
    [ColorPalette.NEUTRAL]: {
      [ColorToken.MAIN]: "#D7D7D7",
      [ColorToken.DARK]: "#A7A7A7",
      [ColorToken.LIGHT]: "#F1F1F1",
      [ColorToken.CONTRAST_TEXT]: "#262626",
    },
    [ColorPalette.TEXT]: {
      [ColorToken.MAIN]: "#393939",
      [ColorToken.DARK]: "#262626",
      [ColorToken.LIGHT]: "#626262",
      [ColorToken.CONTRAST_TEXT]: "#A7A7A7",
    },
    [ColorPalette.ERROR]: {
      [ColorToken.MAIN]: "#FC5038",
      [ColorToken.DARK]: "#C01211",
      [ColorToken.LIGHT]: "#FCA793",
      [ColorToken.CONTRAST_TEXT]: "#FBEEEA",
    },
    [ColorPalette.WARNING]: {
      [ColorToken.MAIN]: "#F4B103",
      [ColorToken.DARK]: "#D79C00",
      [ColorToken.LIGHT]: "#FCCF82",
      [ColorToken.CONTRAST_TEXT]: "#FCEFDC",
    },
    [ColorPalette.SUCCESS]: {
      [ColorToken.MAIN]: "#69B961",
      [ColorToken.DARK]: "#53934C",
      [ColorToken.LIGHT]: "#78D270",
      [ColorToken.CONTRAST_TEXT]: "#DEF8DA",
    },
    [ColorPalette.INFO]: {
      [ColorToken.MAIN]: "#4FB0FB",
      [ColorToken.DARK]: "#1E98E4",
      [ColorToken.LIGHT]: "#8CC5FC",
      [ColorToken.CONTRAST_TEXT]: "#EAF1FB",
    },
    [ColorPalette.BACKGROUND_COLOR]: {
      [ColorToken.MAIN]: "#FFFFFF",
      [ColorToken.DARK]: "#FFFFFF",
      [ColorToken.LIGHT]: "#FFFFFF",
      [ColorToken.CONTRAST_TEXT]: "#FFFFFF",
      [ColorToken.COMPONENT]: "#FFFFFF",
      [ColorToken.SECTION]: "#F2FBFE",
      [ColorToken.SCREEN]: "#FFFFFF",
      [ColorToken.GRADIENT]:
        "linear-gradient(to right, #F5F7FD 0%, #FAFAFA 100%)",
    },
  },
  [ColorThemeToken.DARK]: {
    [ColorPalette.PRIMARY]: {
      [ColorToken.MAIN]: "#2B478B",
      [ColorToken.DARK]: "#213870",
      [ColorToken.LIGHT]: "#738DE4",
      [ColorToken.CONTRAST_TEXT]: "#FFFFFF",
    },
    [ColorPalette.SECONDARY]: {
      [ColorToken.MAIN]: "#17244B",
      [ColorToken.DARK]: "#3C60B7",
      [ColorToken.LIGHT]: "#213870",
      [ColorToken.CONTRAST_TEXT]: "#D2D6F3",
    },
    [ColorPalette.NEUTRAL]: {
      [ColorToken.MAIN]: "#626262",
      [ColorToken.DARK]: "#393939",
      [ColorToken.LIGHT]: "#8F8F8F",
      [ColorToken.CONTRAST_TEXT]: "#F5F5F5",
    },
    [ColorPalette.TEXT]: {
      [ColorToken.MAIN]: "#F1F1F1",
      [ColorToken.DARK]: "#F5F5F5",
      [ColorToken.LIGHT]: "#BEBEBE",
      [ColorToken.CONTRAST_TEXT]: "#8F8F8F",
    },
    [ColorPalette.ERROR]: {
      [ColorToken.MAIN]: "#FC5038",
      [ColorToken.DARK]: "#C01211",
      [ColorToken.LIGHT]: "#FCA793",
      [ColorToken.CONTRAST_TEXT]: "#9B0C0A",
    },
    [ColorPalette.WARNING]: {
      [ColorToken.MAIN]: "#F4B103",
      [ColorToken.DARK]: "#D79C00",
      [ColorToken.LIGHT]: "#FCCF82",
      [ColorToken.CONTRAST_TEXT]: "#9C6F02",
    },
    [ColorPalette.SUCCESS]: {
      [ColorToken.MAIN]: "#69B961",
      [ColorToken.DARK]: "#53934C",
      [ColorToken.LIGHT]: "#78D270",
      [ColorToken.CONTRAST_TEXT]: "#2E572A",
    },
    [ColorPalette.INFO]: {
      [ColorToken.MAIN]: "#4FB0FB",
      [ColorToken.DARK]: "#1E98E4",
      [ColorToken.LIGHT]: "#8CC5FC",
      [ColorToken.CONTRAST_TEXT]: "#0D527F",
    },
    [ColorPalette.BACKGROUND_COLOR]: {
      [ColorToken.MAIN]: "#192440",
      [ColorToken.DARK]: "#192440",
      [ColorToken.LIGHT]: "#192440",
      [ColorToken.CONTRAST_TEXT]: "#192440",
      [ColorToken.COMPONENT]: "#192440",
      [ColorToken.SECTION]: "#192440",
      [ColorToken.SCREEN]: "#17244B",
      [ColorToken.GRADIENT]:
        "linear-gradient(to right, #334067 0%, #384364 100%)",
    },
  },
};

export const useColorSchema = (): Record<
  ColorPalette,
  SimplePaletteColorOptions
> => {
  const theme = useTheme();
  // Derived from theme.palette so the returned schema follows the active
  // mode automatically. We intentionally avoid reading theme.palette.mode
  // here because, under MUI v7's colorSchemes / CSS variables mode, it is
  // a constant and never reflects the user's active scheme. Palette values
  // resolve to var(--mui-palette-*-*) refs that switch via the
  // @media (prefers-color-scheme) query.
  //
  // The text mapping below preserves the original ColorSchema.text shape
  // (main/dark/light/contrastText) so existing callers like
  // `colorSchema.text.contrastText` continue to resolve correctly. See
  // packages/ui-config/src/muiTheme/colors.ts for the inverse mapping.
  const text: SimplePaletteColorOptions = {
    main: theme.palette.text.main ?? theme.palette.text.primary,
    dark: theme.palette.text.primary,
    light: theme.palette.text.secondary,
    contrastText: theme.palette.text.disabled,
  };

  return {
    [ColorPalette.PRIMARY]: theme.palette.primary,
    [ColorPalette.SECONDARY]: theme.palette.secondary,
    [ColorPalette.NEUTRAL]: theme.palette.neutral,
    [ColorPalette.TEXT]: text,
    [ColorPalette.ERROR]: theme.palette.error,
    [ColorPalette.WARNING]: theme.palette.warning,
    [ColorPalette.SUCCESS]: theme.palette.success,
    [ColorPalette.INFO]: theme.palette.info,
    [ColorPalette.BACKGROUND_COLOR]: theme.palette.backgroundColor,
  };
};
